import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    appendTranscribedText,
    getPreferredRecordingMimeType,
    MAX_RECORDING_DURATION_MS,
    transcribeAudioBlob,
    VOICE_INPUT_ERROR_MESSAGE,
} from '@apps/web/components/shared/messages/voice_input_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useMode } from '@apps/web/lib/mode_store';

type VoiceInputStopReason = 'manual' | 'max_duration';

type UseVoiceInputOptions = {
    readonly organizationId: string;
    readonly currentText: string;
    readonly onTranscribed: (appendedText: string) => void;
    readonly composer: string;
    readonly voiceInputFlow: string;
    readonly disabled?: boolean;
};

type UseVoiceInputReturn = {
    readonly canRecordAudio: boolean;
    readonly isRecording: boolean;
    readonly isTranscribing: boolean;
    readonly voiceInputError: string | null;
    readonly startRecording: () => Promise<void>;
    readonly stopRecording: () => void;
    readonly discardPendingTranscription: () => void;
};

export function useVoiceInput({
    organizationId,
    currentText,
    onTranscribed,
    composer,
    voiceInputFlow,
    disabled = false,
}: UseVoiceInputOptions): UseVoiceInputReturn {
    const posthog = usePostHog();
    const mode = useMode();

    const [canRecordAudio, setCanRecordAudio] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [voiceInputError, setVoiceInputError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Array<Blob>>([]);
    const recordingTimeoutRef = useRef<number | null>(null);
    const recordingStartedAtRef = useRef<number | null>(null);
    const recordingStopReasonRef = useRef<VoiceInputStopReason>('manual');
    const transcriptionGenerationRef = useRef(0);

    // Stash latest values in refs so handleRecordingStop captures current text
    const currentTextRef = useRef(currentText);
    currentTextRef.current = currentText;
    const onTranscribedRef = useRef(onTranscribed);
    onTranscribedRef.current = onTranscribed;
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;

    // When the consumer disables the hook (e.g. on submit), automatically
    // invalidate any in-flight transcription so stale results are discarded.
    useEffect(() => {
        if (disabled) {
            transcriptionGenerationRef.current += 1;
        }
    }, [disabled]);

    const clearRecordingTimeout = useCallback(() => {
        if (recordingTimeoutRef.current === null) {
            return;
        }

        window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
    }, []);

    const stopMediaStream = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => {
            track.stop();
        });
        mediaStreamRef.current = null;
    }, []);

    useEffect(() => {
        setCanRecordAudio(
            typeof MediaRecorder !== 'undefined' &&
                typeof navigator !== 'undefined' &&
                Boolean(navigator.mediaDevices?.getUserMedia),
        );
    }, []);

    useEffect(() => {
        if (!disabled) {
            return;
        }

        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state === 'recording') {
            recordingStopReasonRef.current = 'manual';
            recorder.stop();
        }
    }, [disabled]);

    useEffect(() => {
        return () => {
            clearRecordingTimeout();

            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.ondataavailable = null;
                recorder.onstop = null;
                recorder.stop();
            }

            stopMediaStream();
        };
    }, [clearRecordingTimeout, stopMediaStream]);

    const handleRecordingStop = useCallback(
        async (mimeType: string) => {
            clearRecordingTimeout();
            setIsRecording(false);
            mediaRecorderRef.current = null;

            const draftLength = currentTextRef.current.trim().length;
            const stopReason = recordingStopReasonRef.current;
            const recordingDurationMs =
                recordingStartedAtRef.current === null
                    ? undefined
                    : Math.round(
                          performance.now() - recordingStartedAtRef.current,
                      );
            recordingStartedAtRef.current = null;
            const audioChunks = [...audioChunksRef.current];
            audioChunksRef.current = [];
            stopMediaStream();
            const audioBlob = new Blob(audioChunks, { type: mimeType });

            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STOPPED, {
                audio_size_bytes: audioBlob.size,
                composer,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                recording_duration_ms: recordingDurationMs,
                stop_reason: stopReason,
                voice_input_flow: voiceInputFlow,
            });

            if (!audioChunks.length) {
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer,
                    draft_length: draftLength,
                    has_existing_draft: draftLength > 0,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stage: 'empty_audio',
                    stop_reason: stopReason,
                    voice_input_flow: voiceInputFlow,
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                return;
            }

            setIsTranscribing(true);
            const generation = transcriptionGenerationRef.current;
            try {
                const transcript = await transcribeAudioBlob({
                    audioBlob,
                    mode,
                    organizationId,
                });
                if (generation !== transcriptionGenerationRef.current) {
                    return;
                }
                const trimmedTranscript = transcript.trim();
                if (!trimmedTranscript) {
                    posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                        audio_size_bytes: audioBlob.size,
                        composer,
                        draft_length: draftLength,
                        has_existing_draft: draftLength > 0,
                        mode,
                        organization_id: organizationId,
                        recording_duration_ms: recordingDurationMs,
                        stage: 'empty_transcript',
                        stop_reason: stopReason,
                        voice_input_flow: voiceInputFlow,
                    });
                    setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                    return;
                }

                if (disabledRef.current) {
                    posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                        audio_size_bytes: audioBlob.size,
                        composer,
                        draft_length: draftLength,
                        has_existing_draft: draftLength > 0,
                        mode,
                        organization_id: organizationId,
                        recording_duration_ms: recordingDurationMs,
                        stage: 'disabled_mid_flight',
                        stop_reason: stopReason,
                        transcript_length: trimmedTranscript.length,
                        transcript_word_count:
                            trimmedTranscript.split(/\s+/).length,
                        voice_input_flow: voiceInputFlow,
                    });
                    setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                    return;
                }

                setVoiceInputError(null);
                const appendedText = appendTranscribedText(
                    currentTextRef.current,
                    transcript,
                );
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_SUCCEEDED, {
                    appended_to_existing_draft: draftLength > 0,
                    audio_size_bytes: audioBlob.size,
                    composer,
                    draft_length: draftLength,
                    draft_length_after_append: appendedText.trim().length,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stop_reason: stopReason,
                    transcript_length: trimmedTranscript.length,
                    transcript_word_count:
                        trimmedTranscript.split(/\s+/).length,
                    voice_input_flow: voiceInputFlow,
                });
                onTranscribedRef.current(appendedText);
            } catch (error) {
                console.error('Transcription failed:', error);
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer,
                    draft_length: draftLength,
                    error_message:
                        error instanceof Error ? error.message : undefined,
                    has_existing_draft: draftLength > 0,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stage: 'transcription_error',
                    stop_reason: stopReason,
                    voice_input_flow: voiceInputFlow,
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
            } finally {
                setIsTranscribing(false);
            }
        },
        [
            clearRecordingTimeout,
            composer,
            mode,
            organizationId,
            posthog,
            stopMediaStream,
            voiceInputFlow,
        ],
    );

    const startRecording = useCallback(async () => {
        if (disabled || !canRecordAudio || isRecording || isTranscribing) {
            return;
        }

        const draftLength = currentTextRef.current.trim().length;
        setVoiceInputError(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            if (disabledRef.current) {
                stream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }
            mediaStreamRef.current = stream;
            const mimeType = getPreferredRecordingMimeType();
            const recorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);
            const recorderMimeType =
                recorder.mimeType || mimeType || 'audio/webm';

            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            recorder.onstop = () => {
                void handleRecordingStop(recorderMimeType);
            };
            recorder.start();

            recordingStartedAtRef.current = performance.now();
            recordingStopReasonRef.current = 'manual';
            setIsRecording(true);
            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STARTED, {
                composer,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                voice_input_flow: voiceInputFlow,
            });
            recordingTimeoutRef.current = window.setTimeout(() => {
                if (recorder.state !== 'recording') {
                    return;
                }

                recordingStopReasonRef.current = 'max_duration';
                recorder.stop();
            }, MAX_RECORDING_DURATION_MS);
        } catch (error) {
            console.error('Failed to start recording:', error);
            posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                composer,
                draft_length: draftLength,
                error_message:
                    error instanceof Error ? error.message : undefined,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                stage: 'recording_start',
                voice_input_flow: voiceInputFlow,
            });
            setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
            stopMediaStream();
            mediaRecorderRef.current = null;
        }
    }, [
        canRecordAudio,
        composer,
        disabled,
        handleRecordingStop,
        isRecording,
        isTranscribing,
        mode,
        organizationId,
        posthog,
        stopMediaStream,
        voiceInputFlow,
    ]);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state !== 'recording') {
            return;
        }

        recordingStopReasonRef.current = 'manual';
        recorder.stop();
    }, []);

    const discardPendingTranscription = useCallback(() => {
        transcriptionGenerationRef.current += 1;
    }, []);

    return {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
        discardPendingTranscription,
    };
}
